import { useCallback, useRef, type RefCallback } from 'react';

import { registraIngombroBasso } from '@/lib/ingombroBasso';
import { useV3 } from '@/lib/v3';

/**
 * L'INGOMBRO IN BASSO di una barra che NON e' la barra delle schede: il campo di scrittura di una conversazione, la riga d'azione di un incontro (v3 fase 2, ingombro del campo di scrittura;
 * accordo di tma-orc e qsolitario-orcr, 21/10). Nella v3 queste rotte NON hanno la barra a quattro voci (ADR-0242 §2), quindi nessuno scriveva `--ingombro-basso` e il bottone dei minigiochi
 * ripiegava sull'inset di sicurezza: partiva SOTTO «Invia». Chi disegna la barra in basso ne scrive l'ingombro (come `TabBar` e `TabBarV3`), e il bottone, che legge solo la variabile, sale da solo.
 *
 * SOLO con la v3 accesa, di norma: spenta, la rotta ha la barra delle schede (che registra la sua altezza) e questo hook non fa niente, quindi non cambia un pixel. L'opzione `ancheSenzaV3` lo accende
 * anche a v3 spenta, per chi sta in basso INSIEME alla barra delle schede (la fascia di un tavolo di carte: mano e azioni): il suo contributo e` la distanza dal suo bordo alto al fondo, che include gia` la barra. Con la v3 le rotte che lo usano sono nelle
 * eccezioni della barra v3 (`BARRA_ECCEZIONI_V3`); se qualcos'altro sta in basso insieme (la barra del torneo, PR 9), la variabile e` il massimo dei contributi (`lib/ingombroBasso.ts`).
 *
 * Il valore e' la distanza dal bordo ALTO dell'elemento al fondo della finestra (altezza della barra piu' cio' che gli sta sotto, l'inset): quello che il bottone deve scavalcare. Si ricalcola con
 * un `ResizeObserver` (la casella cresce col testo) e a resize/rotazione/`visualViewport`; si toglie allo smontaggio e quando l'elemento sparisce. E' una ref a callback: l'elemento puo' montarsi dopo
 * il primo render (la riga d'azione di un incontro compare a dati arrivati).
 * ⚠️ `elementoMisurato` e` una dipendenza della ref a callback: chi lo passa deve dargli un'identita' STABILE (una funzione di modulo, non una freccia nuova a ogni render), o la ref si ricrea a ogni render
 * e l'elemento si rimisura e si ri-registra ogni volta (la stessa regola della prop `Riga` di `AzionePersistente`, riserva (3) di flotta su #780).
 */
export function useIngombroBasso(elementoMisurato?: (el: HTMLElement) => HTMLElement, { ancheSenzaV3 = false }: { ancheSenzaV3?: boolean } = {}): RefCallback<HTMLElement> {
  const v3 = useV3();
  const pulizia = useRef<(() => void) | null>(null);
  return useCallback(
    (el: HTMLElement | null) => {
      pulizia.current?.();
      pulizia.current = null;
      if (!(v3 || ancheSenzaV3) || !el) return;
      // Un contributo al registro (`lib/ingombroBasso.ts`): la variabile e` il massimo fra le barre in basso, non l'ultima a scrivere.
      // `elementoMisurato`: si misura un ALTRO elemento (la riga della barra del torneo misura il contenitore di tutte le righe: cresce quando compare una riga o la nota, e la riga da sola non lo sa).
      const misurato = elementoMisurato ? elementoMisurato(el) : el;
      const contributo = registraIngombroBasso(() => Math.max(0, Math.round(window.innerHeight - misurato.getBoundingClientRect().top)));
      const pubblica = contributo.aggiorna;
      const osservatore = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(pubblica);
      osservatore?.observe(misurato);
      const vv = window.visualViewport;
      window.addEventListener('resize', pubblica);
      window.addEventListener('orientationchange', pubblica);
      vv?.addEventListener('resize', pubblica);
      pulizia.current = () => {
        osservatore?.disconnect();
        window.removeEventListener('resize', pubblica);
        window.removeEventListener('orientationchange', pubblica);
        vv?.removeEventListener('resize', pubblica);
        contributo.rimuovi();
      };
    },
    [v3, ancheSenzaV3, elementoMisurato],
  );
}

/** Lo spazio che il bottone dei minigiochi vuole SOPRA il campo per stare dentro lo schermo: 56 px del disegno (`h-14`) + 12 px di margine (`BottoneMinigiochi`). */
export const SPAZIO_BOTTONE_PX = 68;

/**
 * L'INGOMBRO IN BASSO di un elemento che sta nel corpo che SCORRE (fase 4, PR 3, #834: il campo di scrittura della chat dell'incontro, dentro la scheda che scorre sotto il fondo). `useIngombroBasso` misura al layout e si aggiorna a
 * resize e ResizeObserver, mai allo scorrimento: col campo che scorre la variabile e` una foto presa all'ingresso (il bottone dei minigiochi restava a meta` schermo a campo uscito, o copriva il campo dopo uno scorrimento).
 * Questo contributo vale lo STESSO (`innerHeight − bordo alto`, quello che il bottone deve scavalcare) ma SOLO finche' il bottone dei minigiochi PUO' stargli sopra restando dentro lo schermo (#834, seconda revisione, M1: la proprieta` e` che il bottone
 * sta SEMPRE dentro lo schermo). Il bottone sale a `ingombro + margine` ed e` alto quanto `SPAZIO_BOTTONE_PX` (56 + 12): con il bordo alto del campo sotto quella quota il contributo e` `innerHeight − top` e il bottone sta fra la quota e il campo; a
 * cavallo del bordo alto dello schermo (`top < 0 < bottom`), scorso oltre (`bottom <= 0`), o cosi' in alto che il bottone non ci starebbe sopra (`top < SPAZIO_BOTTONE_PX`), il campo non puo' avere il bottone sotto di se': vale 0 e il bottone torna al suo
 * posto. Sotto la piega (`top >= innerHeight`) il campo non copre niente: 0.
 * Si ricalcola a ogni scorrimento di qualunque contenitore (lo scroll non risale: si ascolta in cattura sul documento) e quando il campo si sposta SENZA scorrimento (M2): un `ResizeObserver` sul campo e su OGNI antenato, perche' cio' che gli sta
 * sopra (un messaggio nuovo, la riga dei nomi, l'ID che arriva, la presenza che sparisce) cambia la misura del contenitore che lo porta, non la sua. Solo con la v3 accesa, come `useIngombroBasso`, e dello stesso registro. ADDITIVO: `useIngombroBasso`
 * non cambia di un byte (e con lui i controlli sul suo sorgente).
 */
export function useIngombroBassoDelCorpo(): RefCallback<HTMLElement> {
  const v3 = useV3();
  const pulizia = useRef<(() => void) | null>(null);
  return useCallback(
    (el: HTMLElement | null) => {
      pulizia.current?.();
      pulizia.current = null;
      if (!v3 || !el) return;
      const contributo = registraIngombroBasso(() => {
        const r = el.getBoundingClientRect();
        if (r.bottom <= 0 || r.top < SPAZIO_BOTTONE_PX || r.top >= window.innerHeight) return 0;
        return Math.max(0, Math.round(window.innerHeight - r.top));
      });
      const pubblica = contributo.aggiorna;
      const osservatore = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(pubblica);
      // Il campo e ogni suo antenato: se cresce o cala cio' che sta sopra il campo, il contenitore che lo porta cambia misura (il campo no) e il contributo si ripubblica senza aspettare uno scorrimento.
      for (let n: HTMLElement | null = el; n; n = n.parentElement) osservatore?.observe(n);
      const vv = window.visualViewport;
      window.addEventListener('resize', pubblica);
      window.addEventListener('orientationchange', pubblica);
      vv?.addEventListener('resize', pubblica);
      // Lo scorrimento arriva a raffica: un ricalcolo per fotogramma (`requestAnimationFrame`), non uno per evento.
      let fotogramma = 0;
      const alloScorrimento = () => {
        if (fotogramma !== 0) return;
        fotogramma = window.requestAnimationFrame(() => {
          fotogramma = 0;
          pubblica();
        });
      };
      document.addEventListener('scroll', alloScorrimento, { capture: true, passive: true });
      pulizia.current = () => {
        if (fotogramma !== 0) window.cancelAnimationFrame(fotogramma);
        osservatore?.disconnect();
        window.removeEventListener('resize', pubblica);
        window.removeEventListener('orientationchange', pubblica);
        vv?.removeEventListener('resize', pubblica);
        document.removeEventListener('scroll', alloScorrimento, { capture: true });
        contributo.rimuovi();
      };
    },
    [v3],
  );
}
